/**
 * TableSignatureCRUD — K3 · Admin Catalogue · Table Signature.
 *
 * Founder ruling 14 Jun 2026 night (K3 approved · rail-validity gate):
 *   "K3 = Table Signature CRUD + Media Upload under OS Admin. Re-shell
 *    of the existing Table archive admin surface · legacy
 *    /api/admin/lc-archive/* stays LIVE."
 *
 * Sibling of EnjoySignatureCRUD (K1) + EnjoyOverlayCRUD (K2). Same UX
 * shape · K3-specific fields (category_render_type, michelin_stars,
 * booking_routes, force_concierge, verified_reservation_url). Reuses
 * K1's ImageUploader.jsx verbatim.
 *
 * Backend endpoints used:
 *   GET    /api/os-admin/catalogue/table-signature
 *   GET    /api/os-admin/catalogue/table-signature/{id}
 *   POST   /api/os-admin/catalogue/table-signature
 *   PATCH  /api/os-admin/catalogue/table-signature/{id}    (If-Match: "<version>")
 *   POST   /api/os-admin/catalogue/table-signature/{id}/publish
 *   POST   /api/os-admin/catalogue/table-signature/{id}/archive
 *   POST   /api/os-admin/catalogue/table-signature/{id}/restore
 *   POST   /api/os-admin/catalogue/table-signature/{id}/clone
 *   POST   /api/os-admin/catalogue/table-signature/{id}/image/upload
 *   DELETE /api/os-admin/catalogue/table-signature/{id}/image
 *   GET    /api/os-admin/catalogue/table-signature/{id}/audit
 *   DELETE /api/os-admin/catalogue/table-signature/{id}  (founder-only)
 */
import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import ImageUploader from "@/pages/admin/os/ImageUploader";

const ROOT = "/os-admin/catalogue/table-signature";

// K3.1 endpoints (15 Jun 2026 PM · founder-approved post-dry-run):
const BACKFILL_RUN = `${ROOT}/backfill-rails`;
const BACKFILL_LIST = `${ROOT}/backfill-candidates`;
const BACKFILL_PROMOTE = (id) => `${ROOT}/backfill-candidates/${id}/promote`;
const BACKFILL_REJECT = (id) => `${ROOT}/backfill-candidates/${id}/reject`;

const STATUS_CHIP = {
    draft: { bg: "#fef3c7", fg: "#78350f", label: "Draft" },
    live: { bg: "#dcfce7", fg: "#14532d", label: "Live" },
    archived: { bg: "#e5e7eb", fg: "#374151", label: "Archived" },
};

const CATEGORY_OPTIONS = [
    "fine_dine", "casual", "bar", "nightlife",
    "cafe", "brunch", "rooftop", "private_dining",
];

const inputStyle = {
    width: "100%", padding: "8px 10px",
    border: "1px solid #d1d5db", borderRadius: 4,
    fontSize: 14, boxSizing: "border-box",
    background: "#fff", color: "#1a2332",
};


function StatusChip({ status }) {
    const c = STATUS_CHIP[status] || STATUS_CHIP.draft;
    return (
        <span
            data-testid={`k3-status-chip-${status}`}
            style={{
                background: c.bg, color: c.fg,
                padding: "2px 8px", borderRadius: 999,
                fontSize: 11, fontWeight: 600,
                letterSpacing: "0.05em", textTransform: "uppercase",
            }}
        >{c.label}</span>
    );
}


function MichelinChip({ stars, status }) {
    if (!stars || stars < 1 || stars > 3) return null;
    const verified = status === "VERIFIED 2026";
    const bg = verified ? "#fff7ed" : "#f3f4f6";
    const fg = verified ? "#9a3412" : "#4b5563";
    const label = verified
        ? `${"\u2605".repeat(stars)} VERIFIED`
        : `${"\u2605".repeat(stars)} SOURCED`;
    return (
        <span
            data-testid="k3-michelin-chip"
            data-michelin-verified={verified ? "true" : "false"}
            style={{
                background: bg, color: fg,
                padding: "2px 8px", borderRadius: 999,
                fontSize: 11, fontWeight: 600, marginRight: 4,
            }}
        >{label}</span>
    );
}


function Field({ label, testid, children }) {
    return (
        <label data-testid={`k3-field-${testid}`} style={{ display: "block", marginBottom: 12 }}>
            <span style={{ display: "block", fontSize: 12, color: "#6b7280", marginBottom: 4, fontWeight: 600 }}>
                {label}
            </span>
            {children}
        </label>
    );
}


function ListView({ items, total, q, onSearchChange, statusFilter, onStatusChange, onOpen, onCreate, loading, flagDisabled, railStateCounts }) {
    return (
        <div data-testid="k3-list-view">
            <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
                <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }} data-testid="k3-title">
                    Table Signature · Catalogue
                </h1>
                <span style={{ color: "#666", fontSize: 13 }}>
                    {total} {total === 1 ? "row" : "rows"}
                </span>
                <div style={{ flex: 1 }} />
                <input
                    type="search"
                    placeholder="Search name / city / country"
                    value={q}
                    onChange={(e) => onSearchChange(e.target.value)}
                    data-testid="k3-search"
                    style={{ ...inputStyle, width: 280 }}
                />
                <select
                    value={statusFilter || ""}
                    onChange={(e) => onStatusChange(e.target.value || null)}
                    data-testid="k3-status-filter"
                    style={{ ...inputStyle, width: 140 }}
                >
                    <option value="">All statuses</option>
                    <option value="draft">Draft</option>
                    <option value="live">Live</option>
                    <option value="archived">Archived</option>
                </select>
                <button
                    type="button"
                    onClick={onCreate}
                    data-testid="k3-create-btn"
                    style={{
                        background: "#111827", color: "white", border: 0,
                        padding: "8px 14px", borderRadius: 4, fontSize: 13,
                        fontWeight: 600, cursor: "pointer",
                    }}
                >+ New table signature</button>
            </div>
            {/* Founder-requested operational counter · 15 Jun 2026 early-morning
                K3 sign-off enhancement. The "blocked" bucket is the
                single most member-facing failure signal — a verified
                restaurant with a broken booking link, live. */}
            {railStateCounts ? (
                <div data-testid="k3-rail-state-counter" style={{
                    display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap",
                    padding: "10px 12px", background: "#fafaf9",
                    border: "1px solid #e7e5e4", borderRadius: 6,
                }}>
                    <div style={{ fontSize: 11, color: "#78716c", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase" }}>
                        Verified Michelin · operational state
                    </div>
                    <div data-testid="k3-counter-total" title="Total VERIFIED 2026 rows in the live catalogue">
                        <b>{railStateCounts.verified_total}</b>{" "}
                        <span style={{ color: "#78716c", fontSize: 12 }}>verified</span>
                    </div>
                    <div data-testid="k3-counter-with-rail"
                         title="Verified rows with a valid booking rail · publish with the booking route"
                         style={{ color: "#166534" }}>
                        <b>{railStateCounts.with_rail}</b>{" "}
                        <span style={{ fontSize: 12 }}>with rail</span>
                    </div>
                    <div data-testid="k3-counter-concierge-route"
                         title="Verified rows with NO public booking rail · route to Request via Concierge® · the most concierge case there is"
                         style={{ color: "#1d4ed8" }}>
                        <b>{railStateCounts.concierge_route}</b>{" "}
                        <span style={{ fontSize: 12 }}>concierge-route</span>
                    </div>
                    <div data-testid="k3-counter-blocked"
                         title="Verified rows with a BROKEN booking rail · live with a dead button · FIX IMMEDIATELY"
                         style={{
                             color: railStateCounts.blocked > 0 ? "#b91c1c" : "#78716c",
                             fontWeight: railStateCounts.blocked > 0 ? 700 : 400,
                         }}>
                        <b>{railStateCounts.blocked}</b>{" "}
                        <span style={{ fontSize: 12 }}>blocked (broken rail)</span>
                    </div>
                </div>
            ) : null}
            {flagDisabled ? (
                <div data-testid="k3-flag-disabled-banner" style={{
                    padding: 12, marginBottom: 16, borderRadius: 4,
                    background: "#fef3c7", color: "#78350f", fontSize: 13,
                }}>
                    K3 (Table Signature CRUD) is not enabled in this environment.
                    Set <code>CATALOGUE_K3_ENABLED=true</code> in
                    <code> /app/backend/.env</code> and restart the backend.
                </div>
            ) : null}
            {loading ? <div data-testid="k3-loading">Loading…</div> : null}
            <div data-testid="k3-grid" style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                gap: 12,
            }}>
                {items.map(it => (
                    <button
                        key={it.id}
                        type="button"
                        onClick={() => onOpen(it.id)}
                        data-testid={`k3-row-${it.id}`}
                        style={{
                            textAlign: "left", padding: 14, border: "1px solid #e5e7eb",
                            borderRadius: 6, background: "white", color: "#1a2332", cursor: "pointer",
                            display: "flex", flexDirection: "column", gap: 8,
                        }}
                    >
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                            <StatusChip status={it.publish_status} />
                            <MichelinChip stars={it.michelin_stars} status={it.michelin_stars_status} />
                            {(() => {
                                // Founder eye-test 15 Jun 2026: a verified row with no
                                // public booking rail is a CONCIERGE® route case ·
                                // surface that intentionally on the list view so the
                                // operator sees the brand-central state at a glance.
                                const verified = it.michelin_stars_status === "VERIFIED 2026";
                                const hasRail = !!(
                                    it.verified_reservation_url
                                    || (it._K3_canonical_rails || []).length
                                );
                                if (verified && !hasRail) {
                                    return (
                                        <span
                                            data-testid={`k3-row-${it.id}-concierge-route`}
                                            title="Verified · no public rail · this one's ours to arrange"
                                            style={{
                                                background: "#eff6ff", color: "#1d4ed8",
                                                padding: "2px 8px", borderRadius: 999,
                                                fontSize: 11, fontWeight: 600,
                                            }}
                                        >&rarr; Concierge&reg;</span>
                                    );
                                }
                                return null;
                            })()}
                            <span style={{ fontSize: 11, color: "#9ca3af" }}>v{it.version || 1}</span>
                        </div>
                        <div style={{ fontWeight: 600, fontSize: 15, lineHeight: 1.3 }}>
                            {it.title || <i>(untitled)</i>}
                        </div>
                        <div style={{ fontSize: 12, color: "#6b7280" }}>
                            {it.category_render_type ? <>{it.category_render_type} · </> : null}
                            {[it.city || it.city_clean, it.country || it.country_clean].filter(Boolean).join(", ")}
                        </div>
                        {it.hero_image_url ? (
                            <div style={{ position: "relative" }}>
                                <img
                                    src={it.hero_image_url} alt=""
                                    data-testid={`k3-row-${it.id}-thumb`}
                                    style={{ width: "100%", height: 80, objectFit: "cover", borderRadius: 4 }}
                                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                                />
                                {it.hero_image_source === "google_places" ? (
                                    <div
                                        data-testid={`k3-row-${it.id}-photo-attr`}
                                        title="Photo from Google Places · attribution carries through to the member card"
                                        style={{
                                            position: "absolute", top: 4, right: 4,
                                            fontSize: 9, fontWeight: 600, letterSpacing: 0.4,
                                            color: "#fff", background: "rgba(0,0,0,0.55)",
                                            padding: "2px 5px", borderRadius: 3,
                                        }}
                                    >
                                        GOOGLE PHOTO
                                    </div>
                                ) : null}
                            </div>
                        ) : (
                            <div style={{
                                fontSize: 11, color: "#92400e",
                                background: "#fef3c7", padding: "4px 6px", borderRadius: 4,
                            }}>No image · cannot publish</div>
                        )}
                    </button>
                ))}
                {(!items.length && !loading) ? (
                    <div data-testid="k3-empty" style={{ color: "#9ca3af", padding: 24, textAlign: "center" }}>
                        No rows match your filter.
                    </div>
                ) : null}
            </div>
        </div>
    );
}


function CreateForm({ onCreated, onCancel }) {
    const [draft, setDraft] = useState({
        title: "", category_render_type: "fine_dine",
        city: "", country: "", concierge_whisper: "",
    });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);

    const handle = (k, v) => setDraft(d => ({ ...d, [k]: v }));

    const submit = async () => {
        setBusy(true); setErr(null);
        try {
            const body = Object.fromEntries(
                Object.entries(draft).filter(([_, v]) => v && String(v).trim()),
            );
            const r = await api.post(ROOT, body);
            onCreated(r.data.id);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (detail?.error === "validation_failed") {
                setErr("Validation: " + (detail.violations || []).join(" · "));
            } else {
                setErr(formatApiError(detail) || e.message);
            }
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="k3-create-form" style={{ maxWidth: 560 }}>
            <h2 style={{ marginTop: 0 }}>New table signature</h2>
            {err ? (
                <div data-testid="k3-create-error" style={{
                    padding: 8, background: "#fdecea", color: "#a02020",
                    borderRadius: 4, marginBottom: 8, fontSize: 13,
                }}>{err}</div>
            ) : null}
            <Field label="Title" testid="title">
                <input value={draft.title} onChange={e => handle("title", e.target.value)}
                       data-testid="k3-create-title" style={inputStyle} />
            </Field>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <Field label="Category" testid="category_render_type">
                    <select value={draft.category_render_type}
                            onChange={e => handle("category_render_type", e.target.value)}
                            data-testid="k3-create-category" style={inputStyle}>
                        {CATEGORY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                </Field>
                <Field label="Country (ISO-2)" testid="country">
                    <input value={draft.country} onChange={e => handle("country", e.target.value.toUpperCase())}
                           maxLength={3} data-testid="k3-create-country" style={inputStyle} />
                </Field>
            </div>
            <Field label="City" testid="city">
                <input value={draft.city} onChange={e => handle("city", e.target.value)}
                       data-testid="k3-create-city" style={inputStyle} />
            </Field>
            <Field label="Concierge whisper (member-facing · ≤ 280 chars)" testid="whisper">
                <textarea value={draft.concierge_whisper} maxLength={280}
                          onChange={e => handle("concierge_whisper", e.target.value)}
                          data-testid="k3-create-whisper"
                          style={{ ...inputStyle, minHeight: 80, fontFamily: "inherit" }} />
            </Field>
            <div style={{ display: "flex", gap: 8 }}>
                <button onClick={submit} disabled={busy || !draft.title}
                        data-testid="k3-create-submit"
                        style={{
                            background: "#111827", color: "white", border: 0,
                            padding: "8px 14px", borderRadius: 4, fontSize: 13,
                            fontWeight: 600, cursor: "pointer",
                            opacity: (busy || !draft.title) ? 0.5 : 1,
                        }}>
                    {busy ? "Creating…" : "Create draft"}
                </button>
                <button onClick={onCancel} data-testid="k3-create-cancel"
                        style={{
                            background: "white", color: "#374151",
                            border: "1px solid #d1d5db",
                            padding: "8px 14px", borderRadius: 4, fontSize: 13,
                            cursor: "pointer",
                        }}>Cancel</button>
            </div>
        </div>
    );
}


function RailEditor({ rails, onChange }) {
    const add = () => onChange([...(rails || []), { platform: "", url: "" }]);
    const update = (idx, field, val) => {
        const next = [...(rails || [])];
        next[idx] = { ...next[idx], [field]: val };
        onChange(next);
    };
    const remove = (idx) => {
        const next = [...(rails || [])];
        next.splice(idx, 1);
        onChange(next);
    };
    return (
        <div data-testid="k3-rail-editor">
            {(rails || []).map((r, idx) => (
                <div key={idx} data-testid={`k3-rail-row-${idx}`}
                     style={{ display: "grid", gridTemplateColumns: "1fr 2fr auto", gap: 8, marginBottom: 6 }}>
                    <input value={r.platform || ""} placeholder="platform (e.g. SevenRooms)"
                           onChange={e => update(idx, "platform", e.target.value)}
                           data-testid={`k3-rail-platform-${idx}`} style={inputStyle} />
                    <input value={r.url || ""} placeholder="https://..."
                           onChange={e => update(idx, "url", e.target.value)}
                           data-testid={`k3-rail-url-${idx}`} style={inputStyle} />
                    <button type="button" onClick={() => remove(idx)}
                            data-testid={`k3-rail-remove-${idx}`}
                            style={{ background: "transparent", color: "#a02020",
                                     border: "1px solid #d1d5db", borderRadius: 4,
                                     padding: "6px 10px", cursor: "pointer" }}>×</button>
                </div>
            ))}
            <button type="button" onClick={add} data-testid="k3-rail-add"
                    style={{ background: "white", color: "#374151",
                             border: "1px dashed #d1d5db", borderRadius: 4,
                             padding: "6px 10px", fontSize: 12, cursor: "pointer" }}>
                + Add booking rail
            </button>
            <div style={{ marginTop: 8, fontSize: 11, color: "#6b7280", lineHeight: 1.5 }}>
                <b style={{ color: "#1d4ed8" }}>No rail</b> is a clean publishable state ·
                the card routes to "Request via Concierge\u00ae" and the desk holds the booking.
                For a Michelin-starred restaurant with no public booking link, this is the
                most concierge case there is — "this one's ours to arrange."
                <br />
                A rail with a <b style={{ color: "#b91c1c" }}>broken</b> URL will be blocked at
                publish — a card must never ship with a dead button.
            </div>
        </div>
    );
}


function DetailView({ rowId, onBack, onChanged }) {
    const [row, setRow] = useState(null);
    const [draft, setDraft] = useState({});
    const [dirty, setDirty] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [audit, setAudit] = useState([]);

    const refresh = useCallback(async () => {
        try {
            const r = await api.get(`${ROOT}/${rowId}`);
            setRow(r.data);
            setDraft({ ...r.data });
            setDirty(false);
            const a = await api.get(`${ROOT}/${rowId}/audit`);
            setAudit(a.data.items || []);
        } catch (e) {
            setError(formatApiError(e?.response?.data?.detail) || e.message);
        }
    }, [rowId]);

    useEffect(() => { refresh(); }, [refresh]);

    const handleField = (k, v) => {
        setDraft(d => ({ ...d, [k]: v }));
        setDirty(true);
    };

    const save = async () => {
        if (!row || !dirty) return;
        setBusy(true); setError(null);
        try {
            const patch = {};
            Object.entries(draft).forEach(([k, v]) => {
                const a = row[k];
                const b = v;
                if (JSON.stringify(a || null) !== JSON.stringify(b || null)) {
                    patch[k] = v;
                }
            });
            if (!Object.keys(patch).length) {
                setDirty(false); setBusy(false); return;
            }
            await api.patch(`${ROOT}/${rowId}`, patch, {
                headers: { "If-Match": `"${row.version}"` },
            });
            await refresh();
            onChanged?.();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (detail?.error === "version_conflict") {
                setError("Row was edited by another operator. Refresh and re-apply.");
            } else if (detail?.error === "validation_failed") {
                setError("Validation failed: " + (detail.violations || []).join(" · "));
            } else {
                setError(formatApiError(detail) || e.message);
            }
        } finally {
            setBusy(false);
        }
    };

    const callAction = async (action, label) => {
        if (!row) return;
        setBusy(true); setError(null);
        try {
            await api.post(`${ROOT}/${rowId}/${action}`);
            await refresh();
            onChanged?.();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (detail?.error === "publish_blocked") {
                const blocks = [
                    ...(detail.blocks || []),
                    ...(detail.gate_missing_fields || []).map(f => `${f}`),
                ];
                setError(`Publish blocked: ${blocks.join(" · ")}`);
            } else {
                setError(formatApiError(detail) || e.message || `${label} failed`);
            }
        } finally { setBusy(false); }
    };

    const handleImageUpload = async (file) => {
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`${ROOT}/${rowId}/image/upload`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
        });
        await refresh();
        onChanged?.();
    };

    const handleImageRemove = async () => {
        await api.delete(`${ROOT}/${rowId}/image`);
        await refresh();
        onChanged?.();
    };

    if (!row) {
        return (
            <div data-testid="k3-detail-loading">
                <button onClick={onBack} data-testid="k3-detail-back-btn" style={{ marginBottom: 16 }}>← Back to list</button>
                {error ? <div style={{ color: "#a02020" }}>{error}</div> : <div>Loading…</div>}
            </div>
        );
    }

    return (
        <div data-testid="k3-detail-view">
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                <button onClick={onBack} data-testid="k3-detail-back-btn"
                        style={{ padding: "6px 10px", border: "1px solid #ccc", borderRadius: 4, background: "white", cursor: "pointer" }}>
                    ← Back to list
                </button>
                <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, flex: 1 }} data-testid="k3-detail-title">
                    {row.title || <i>Untitled</i>}
                </h1>
                <MichelinChip stars={row.michelin_stars} status={row.michelin_stars_status} />
                <StatusChip status={row.publish_status} />
                <span style={{ fontSize: 12, color: "#999" }}>v{row.version}</span>
            </div>

            {error ? (
                <div data-testid="k3-detail-error" style={{
                    padding: 10, marginBottom: 12, borderRadius: 4,
                    background: "#fdecea", color: "#a02020",
                }}>{error}</div>
            ) : null}

            <div style={{ display: "grid", gridTemplateColumns: "1fr 380px", gap: 24 }}>
                {/* LEFT · the form */}
                <div data-testid="k3-form">
                    <Field label="Title" testid="title">
                        <input value={draft.title || ""} onChange={e => handleField("title", e.target.value)}
                               data-testid="k3-input-title" style={inputStyle} />
                    </Field>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                        <Field label="Category" testid="category_render_type">
                            <select value={draft.category_render_type || "fine_dine"}
                                    onChange={e => handleField("category_render_type", e.target.value)}
                                    data-testid="k3-input-category" style={inputStyle}>
                                {CATEGORY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </Field>
                        <Field label="Subcategory" testid="subcategory">
                            <input value={draft.subcategory || ""}
                                   onChange={e => handleField("subcategory", e.target.value)}
                                   data-testid="k3-input-subcategory" style={inputStyle} />
                        </Field>
                        <Field label="Country (ISO-2)" testid="country">
                            <input value={draft.country || ""} onChange={e => handleField("country", e.target.value.toUpperCase())}
                                   maxLength={3} data-testid="k3-input-country" style={inputStyle} />
                        </Field>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                        <Field label="City" testid="city">
                            <input value={draft.city || ""} onChange={e => handleField("city", e.target.value)}
                                   data-testid="k3-input-city" style={inputStyle} />
                        </Field>
                        <Field label="Destination / area" testid="destination">
                            <input value={draft.destination || ""}
                                   onChange={e => handleField("destination", e.target.value)}
                                   data-testid="k3-input-destination" style={inputStyle} />
                        </Field>
                    </div>
                    <Field label="Concierge whisper (member-facing · ≤ 280 chars)" testid="whisper">
                        <textarea value={draft.concierge_whisper || ""} maxLength={280}
                                  onChange={e => handleField("concierge_whisper", e.target.value)}
                                  data-testid="k3-input-whisper"
                                  style={{ ...inputStyle, minHeight: 80, fontFamily: "inherit" }} />
                        <div style={{ fontSize: 11, color: "#999", textAlign: "right" }}>
                            {(draft.concierge_whisper || "").length}/280
                        </div>
                    </Field>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                        <Field label="Michelin stars (0-3)" testid="michelin_stars">
                            <select value={draft.michelin_stars ?? ""}
                                    onChange={e => handleField("michelin_stars", e.target.value ? parseInt(e.target.value, 10) : null)}
                                    data-testid="k3-input-michelin-stars" style={inputStyle}>
                                <option value="">—</option>
                                <option value="0">0</option>
                                <option value="1">1</option>
                                <option value="2">2</option>
                                <option value="3">3</option>
                            </select>
                        </Field>
                        <Field label="Michelin status" testid="michelin_stars_status">
                            <select value={draft.michelin_stars_status || ""}
                                    onChange={e => handleField("michelin_stars_status", e.target.value || null)}
                                    data-testid="k3-input-michelin-status" style={inputStyle}>
                                <option value="">—</option>
                                <option value="SOURCED">SOURCED</option>
                                <option value="VERIFIED 2026">VERIFIED 2026</option>
                            </select>
                        </Field>
                        <Field label="Cuisine label" testid="cuisine_label">
                            <input value={draft.cuisine_label || ""}
                                   onChange={e => handleField("cuisine_label", e.target.value)}
                                   data-testid="k3-input-cuisine" style={inputStyle} />
                        </Field>
                    </div>
                    <Field label="Booking rails" testid="booking_routes">
                        <RailEditor rails={draft.booking_routes || []}
                                    onChange={v => handleField("booking_routes", v)} />
                    </Field>
                    {/* 15 Jun 2026 founder-ordered: surface the picker's
                        chosen primary so the operator sees the registry's
                        deterministic winner. This is the rail the member
                        will land on · the rest are visible-but-demoted. */}
                    {row._K3_primary_rail ? (
                        <div data-testid="k3-primary-rail-badge" style={{
                            marginTop: -6, marginBottom: 12,
                            padding: "8px 10px", background: "#f0fdf4",
                            border: "1px solid #bbf7d0", borderRadius: 4,
                            fontSize: 12, color: "#166534",
                        }}>
                            <b>Primary rail</b> · {row._K3_primary_rail.label}
                            {row._K3_primary_rail_explanation && row._K3_primary_rail_explanation !== "only candidate" ? (
                                <span style={{ color: "#15803d", marginLeft: 6, fontStyle: "italic" }}>
                                    ({row._K3_primary_rail_explanation})
                                </span>
                            ) : null}
                            {" · "}
                            <span style={{ color: "#15803d", fontFamily: "monospace", fontSize: 11 }}>
                                rank {row._K3_primary_rail.priority_rank}
                            </span>
                            {row._K3_primary_rail.kind === "google_reserve" ? (
                                <span style={{ color: "#92400e", marginLeft: 8, fontSize: 11 }}>
                                    ⚠ generic fallback · check for a regional rail
                                </span>
                            ) : null}
                            {row._K3_primary_rail.kind === "concierge" ? (
                                <span style={{ color: "#1d4ed8", marginLeft: 8, fontSize: 11 }}>
                                    · routes to Concierge®
                                </span>
                            ) : null}
                            {row._K3_country_resolved && row._K3_country_resolution_source ? (
                                <div style={{ marginTop: 4, fontSize: 10, color: "#65a30d" }}>
                                    country resolved: <b>{row._K3_country_resolved}</b>
                                    {" · "}
                                    <span style={{ fontFamily: "monospace" }}>
                                        {row._K3_country_resolution_source}
                                    </span>
                                </div>
                            ) : null}
                        </div>
                    ) : null}

                    {/* 15 Jun 2026 picker-refinement · reviewer reference
                        panel. Two clickable links the human reviewer uses
                        to verify a candidate against authoritative
                        sources, in their own browser. NO automated request
                        from our backend · personal-use compliant. */}
                    {(row.title || row.original_archive_name || row.resolved_name) ? (
                        <div data-testid="k3-reviewer-references" style={{
                            marginTop: 4, marginBottom: 12,
                            padding: "10px 12px", background: "#fffbeb",
                            border: "1px solid #fde68a", borderRadius: 4,
                            fontSize: 12, color: "#78350f",
                        }}>
                            <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase" }}>
                                Reviewer references
                            </div>
                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                <a
                                    href={`https://guide.michelin.com/en/restaurants?searchText=${encodeURIComponent(row.title || row.resolved_name || row.original_archive_name || "")}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="k3-reviewer-link-michelin"
                                    style={{
                                        padding: "4px 10px", background: "white",
                                        border: "1px solid #fde68a", borderRadius: 4,
                                        color: "#92400e", textDecoration: "none", fontWeight: 600,
                                    }}
                                >Michelin Guide ↗</a>
                                <a
                                    href={`https://www.google.com/maps/search/${encodeURIComponent(((row.title || row.resolved_name || row.original_archive_name || "") + " " + (row.city || row.city_clean || "")).trim())}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="k3-reviewer-link-google-maps"
                                    style={{
                                        padding: "4px 10px", background: "white",
                                        border: "1px solid #fde68a", borderRadius: 4,
                                        color: "#92400e", textDecoration: "none", fontWeight: 600,
                                    }}
                                >Google Maps ↗</a>
                            </div>
                            <div style={{ marginTop: 8, fontSize: 11, color: "#78350f", lineHeight: 1.5 }}>
                                <b>Rail-discovery workflow</b>: open Michelin → click <i>"Reserve a table"</i> →
                                note the platform it hands to → <b>strip any <code>?utm_*</code> or <code>?source=michelin</code> parameter</b> →
                                save the clean platform URL. This keeps our catalogue free of affiliate routing.
                            </div>
                        </div>
                    ) : null}

                    <label style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12 }}
                           data-testid="k3-field-force_concierge">
                        <input type="checkbox" checked={!!draft.force_concierge}
                               onChange={e => handleField("force_concierge", e.target.checked)}
                               data-testid="k3-input-force-concierge" />
                        <span style={{ fontSize: 13 }}>Force routing through Concierge® desk (rails ignored)</span>
                    </label>

                    <div style={{ display: "flex", gap: 8, marginTop: 16, flexWrap: "wrap" }}>
                        <button onClick={save} disabled={!dirty || busy}
                                data-testid="k3-save-btn"
                                style={{
                                    background: dirty ? "#111827" : "#9ca3af",
                                    color: "white", border: 0,
                                    padding: "8px 14px", borderRadius: 4, fontSize: 13,
                                    fontWeight: 600, cursor: dirty ? "pointer" : "default",
                                }}>
                            {busy ? "Saving…" : "Save changes"}
                        </button>
                        {row.publish_status !== "live" ? (
                            <button onClick={() => callAction("publish", "Publish")}
                                    disabled={busy}
                                    data-testid="k3-publish-btn"
                                    style={{
                                        background: "#059669", color: "white", border: 0,
                                        padding: "8px 14px", borderRadius: 4, fontSize: 13,
                                        fontWeight: 600, cursor: "pointer",
                                    }}>Publish</button>
                        ) : null}
                        {row.publish_status !== "archived" ? (
                            <button onClick={() => callAction("archive", "Archive")}
                                    disabled={busy}
                                    data-testid="k3-archive-btn"
                                    style={{
                                        background: "white", color: "#374151",
                                        border: "1px solid #d1d5db",
                                        padding: "8px 14px", borderRadius: 4, fontSize: 13,
                                        cursor: "pointer",
                                    }}>Archive</button>
                        ) : (
                            <button onClick={() => callAction("restore", "Restore")}
                                    disabled={busy}
                                    data-testid="k3-restore-btn"
                                    style={{
                                        background: "white", color: "#374151",
                                        border: "1px solid #d1d5db",
                                        padding: "8px 14px", borderRadius: 4, fontSize: 13,
                                        cursor: "pointer",
                                    }}>Restore</button>
                        )}
                        <button onClick={() => callAction("clone", "Clone")}
                                disabled={busy || row.publish_status === "archived"}
                                data-testid="k3-clone-btn"
                                style={{
                                    background: "white", color: "#374151",
                                    border: "1px solid #d1d5db",
                                    padding: "8px 14px", borderRadius: 4, fontSize: 13,
                                    cursor: "pointer",
                                }}>Clone</button>
                    </div>
                </div>

                {/* RIGHT · image + audit */}
                <div data-testid="k3-side">
                    <Field label="Hero image" testid="image">
                        <ImageUploader
                            currentImageUrl={row.image_url}
                            onUpload={handleImageUpload}
                            onRemove={handleImageRemove}
                            testidPrefix="k3-image"
                        />
                    </Field>
                    <div data-testid="k3-audit-list" style={{ marginTop: 16, fontSize: 12 }}>
                        <div style={{ fontWeight: 600, color: "#374151", marginBottom: 6 }}>Audit trail</div>
                        <ul style={{ padding: 0, margin: 0, listStyle: "none", maxHeight: 240, overflowY: "auto" }}>
                            {audit.slice(0, 20).map((it, i) => (
                                <li key={i} style={{ padding: "4px 0", borderBottom: "1px dashed #eee" }}>
                                    <code style={{ fontSize: 11 }}>{it.event}</code>
                                    {" · "}
                                    <span style={{ color: "#6b7280" }}>{it.actor}</span>
                                    {" · "}
                                    <time style={{ color: "#9ca3af" }}>{(it.at || "").slice(0, 19).replace("T", " ")}</time>
                                </li>
                            ))}
                            {!audit.length ? <li style={{ color: "#9ca3af" }}>No audit history yet.</li> : null}
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
}


export default function TableSignatureCRUD() {
    const [view, setView] = useState("list");        // "list" | "create" | "detail" | "backfill"
    const [activeId, setActiveId] = useState(null);
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [railStateCounts, setRailStateCounts] = useState(null);
    const [q, setQ] = useState("");
    const [statusFilter, setStatusFilter] = useState(null);
    const [loading, setLoading] = useState(false);
    const [flagDisabled, setFlagDisabled] = useState(false);

    const refresh = useCallback(async () => {
        setLoading(true); setFlagDisabled(false);
        try {
            const params = { limit: 60 };
            if (q) params.q = q;
            if (statusFilter) params.publish_status = statusFilter;
            const r = await api.get(ROOT, { params });
            setItems(r.data.items || []);
            setTotal(r.data.total || 0);
            setRailStateCounts(r.data.rail_state_counts || null);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (detail?.error === "catalogue_k3_disabled") {
                setFlagDisabled(true);
                setItems([]); setTotal(0);
            } else {
                console.error("K3 list failed:", e);
            }
        } finally {
            setLoading(false);
        }
    }, [q, statusFilter]);

    useEffect(() => { refresh(); }, [refresh]);

    return (
        <div data-testid="k3-table-signature-crud" className="os-light-surface" style={{ padding: 24 }}>
            {view === "list" && (
                <>
                    <div style={{ display: "flex", gap: 8, marginBottom: 8, justifyContent: "flex-end" }}>
                        <button
                            type="button"
                            onClick={() => setView("backfill")}
                            data-testid="k3-1-open-backfill-review-btn"
                            style={{
                                background: "white", color: "#1d4ed8",
                                border: "1px solid #bfdbfe", borderRadius: 4,
                                padding: "6px 12px", fontSize: 12, fontWeight: 600,
                                cursor: "pointer",
                            }}>
                            K3.1 · Backfill review →
                        </button>
                    </div>
                    <ListView
                        items={items} total={total}
                        railStateCounts={railStateCounts}
                        q={q} onSearchChange={setQ}
                        statusFilter={statusFilter} onStatusChange={setStatusFilter}
                        onOpen={(id) => { setActiveId(id); setView("detail"); }}
                        onCreate={() => setView("create")}
                        loading={loading}
                        flagDisabled={flagDisabled}
                    />
                </>
            )}
            {view === "create" && (
                <CreateForm
                    onCreated={(id) => { setActiveId(id); setView("detail"); refresh(); }}
                    onCancel={() => setView("list")}
                />
            )}
            {view === "detail" && activeId && (
                <DetailView
                    rowId={activeId}
                    onBack={() => { setView("list"); refresh(); }}
                    onChanged={refresh}
                />
            )}
            {view === "backfill" && (
                <BackfillReview
                    onBack={() => { setView("list"); refresh(); }}
                    onOpenRow={(id) => { setActiveId(id); setView("detail"); }}
                />
            )}
        </div>
    );
}


// ════════════════════════════════════════════════════════════════════
// K3.1 · Backfill review panel · 15 Jun 2026 PM (founder-approved)
// ════════════════════════════════════════════════════════════════════
// Inside-the-K3-admin surface · NOT a separate route (founder ruling
// 15 Jun 2026: "review UI inside the K3 admin, reuse the drawer with a
// 'pending candidates' filter, NOT a separate route"). Honours the
// candidate-never-self-publishes contract · the human picks a clean
// URL and the backend promotes via the K3 picker.
function BackfillReview({ onBack, onOpenRow }) {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [flagDisabled, setFlagDisabled] = useState(false);
    const [runResult, setRunResult] = useState(null);
    const [batchSize, setBatchSize] = useState(50);
    const [dryRun, setDryRun] = useState(false);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get(BACKFILL_LIST, { params: { pending_only: true, limit: 50 } });
            setItems(r.data.items || []);
            setTotal(r.data.total || 0);
            setFlagDisabled(false);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (detail?.error === "catalogue_k3_1_backfill_disabled") {
                setFlagDisabled(true);
                setItems([]); setTotal(0);
            } else {
                setError(formatApiError(detail) || e.message);
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    const runBatch = async () => {
        setBusy(true); setError(null);
        try {
            const r = await api.post(BACKFILL_RUN, { batch_size: batchSize, dry_run: dryRun });
            setRunResult(r.data);
            await refresh();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            setError(formatApiError(detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    const promote = async (rowId, kind, confirmedUrl) => {
        if (!confirmedUrl || !confirmedUrl.trim()) {
            setError("Confirmed URL is required to promote.");
            return;
        }
        setBusy(true); setError(null);
        try {
            await api.post(BACKFILL_PROMOTE(rowId), { kind, confirmed_url: confirmedUrl.trim() });
            await refresh();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            setError(formatApiError(detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    const reject = async (rowId, kind, reason) => {
        setBusy(true); setError(null);
        try {
            await api.post(BACKFILL_REJECT(rowId), { kind, reason: reason || null });
            await refresh();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            setError(formatApiError(detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="k3-1-backfill-review">
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                <button onClick={onBack} data-testid="k3-1-backfill-back-btn"
                        style={{ padding: "6px 10px", border: "1px solid #ccc", borderRadius: 4, background: "white", cursor: "pointer" }}>
                    ← Back to list
                </button>
                <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, flex: 1 }} data-testid="k3-1-backfill-title">
                    K3.1 · Backfill candidate review
                </h1>
                <span style={{ fontSize: 12, color: "#6b7280" }} data-testid="k3-1-backfill-total">
                    {total} rows with pending candidates
                </span>
            </div>

            {flagDisabled ? (
                <div data-testid="k3-1-backfill-flag-disabled" style={{
                    padding: 12, marginBottom: 16, borderRadius: 4,
                    background: "#fef3c7", color: "#78350f", fontSize: 13,
                }}>
                    K3.1 backfill is not enabled in this environment. Set
                    <code> CATALOGUE_K3_1_BACKFILL_ENABLED=true</code> in
                    <code> /app/backend/.env</code> and restart the backend.
                </div>
            ) : null}

            {error ? (
                <div data-testid="k3-1-backfill-error" style={{
                    padding: 10, marginBottom: 12, borderRadius: 4,
                    background: "#fdecea", color: "#a02020",
                }}>{error}</div>
            ) : null}

            {/* Run-batch panel · founder-only operation */}
            <div style={{
                padding: 14, marginBottom: 16, borderRadius: 6,
                background: "#f0f9ff", border: "1px solid #bae6fd",
            }} data-testid="k3-1-backfill-run-panel">
                <div style={{ fontWeight: 700, fontSize: 13, color: "#0c4a6e", marginBottom: 8 }}>
                    Generate next batch
                </div>
                <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                    <label style={{ fontSize: 12, color: "#0c4a6e", display: "flex", gap: 6, alignItems: "center" }}>
                        Batch size:
                        <input type="number" min={1} max={200}
                               value={batchSize}
                               onChange={e => setBatchSize(Math.max(1, Math.min(200, parseInt(e.target.value || "50", 10))))}
                               data-testid="k3-1-backfill-batch-size"
                               style={{ ...inputStyle, width: 80 }} />
                    </label>
                    <label style={{ fontSize: 12, color: "#0c4a6e", display: "flex", gap: 6, alignItems: "center" }}>
                        <input type="checkbox" checked={dryRun}
                               onChange={e => setDryRun(e.target.checked)}
                               data-testid="k3-1-backfill-dry-run" />
                        Dry run (compute · do not persist)
                    </label>
                    <button onClick={runBatch} disabled={busy || flagDisabled}
                            data-testid="k3-1-backfill-run-btn"
                            style={{
                                background: busy ? "#9ca3af" : "#0c4a6e",
                                color: "white", border: 0,
                                padding: "8px 14px", borderRadius: 4, fontSize: 13,
                                fontWeight: 600, cursor: busy ? "default" : "pointer",
                            }}>
                        {busy ? "Running…" : "Run batch (highest-traffic first)"}
                    </button>
                </div>
                <div style={{ marginTop: 8, fontSize: 11, color: "#0c4a6e", lineHeight: 1.5 }}>
                    <b>Allowlist</b>: DiningCity · TheFork · Omakase · Chope. Each candidate is a SEARCH-PAGE URL
                    the human reviewer opens to verify the restaurant on the destination platform · the candidate
                    NEVER becomes a live &quot;Reserve&quot; button until promoted with a clean restaurant URL.
                </div>
                {runResult ? (
                    <div data-testid="k3-1-backfill-run-result" style={{
                        marginTop: 10, padding: 12, background: "white",
                        border: "1px solid #bae6fd", borderRadius: 4, fontSize: 12,
                        color: "#0c4a6e",
                    }}>
                        <div><b>Rail-less rows in catalogue:</b> {runResult.rail_less_total}</div>
                        <div><b>Rows processed in this batch:</b> {runResult.rows_processed}</div>
                        <div><b>Rows with new candidates:</b> {runResult.rows_with_candidates}</div>
                        <div><b>Candidates generated:</b> {runResult.candidates_generated}</div>
                        <div><b>Dry run:</b> {runResult.dry_run ? "yes (nothing persisted)" : "no (persisted)"}</div>
                        {runResult.member_route_summary ? (
                            <div data-testid="k3-1-backfill-run-result-mrt" style={{ marginTop: 8, paddingTop: 8, borderTop: "1px dashed #bae6fd" }}>
                                <div style={{ fontWeight: 700, marginBottom: 4 }}>
                                    What members actually see today (from `table_route_log`):
                                </div>
                                {Object.entries(runResult.member_route_summary)
                                    .sort((a, b) => b[1] - a[1])
                                    .map(([status, n]) => (
                                    <div key={status} style={{ marginLeft: 8, fontSize: 11 }}>
                                        · <b>{n}</b> rows · {{
                                            covered: "ALREADY rendering a real third-party rail via sniffer · K3.1 redundant",
                                            google_reserve: "Google Reserve fallback · K3.1 would upgrade",
                                            phone: "Call route · K3.1 would upgrade to one-tap booking",
                                            contact: "Venue website route · K3.1 would upgrade",
                                            concierge: "Concierge® route · no public button · K3.1 most valuable here",
                                            no_log_yet: "No render logged yet · status unknown until a member opens the row",
                                        }[status] || status}
                                    </div>
                                ))}
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </div>

            {loading ? <div data-testid="k3-1-backfill-loading">Loading…</div> : null}

            <div data-testid="k3-1-backfill-pending-rows">
                {items.map((row) => (
                    <RowCandidateCard
                        key={row.id}
                        row={row}
                        busy={busy}
                        onPromote={promote}
                        onReject={reject}
                        onOpenRow={() => onOpenRow(row.id)}
                    />
                ))}
                {(!items.length && !loading && !flagDisabled) ? (
                    <div data-testid="k3-1-backfill-empty" style={{ color: "#9ca3af", padding: 24, textAlign: "center" }}>
                        No rows carry pending candidates · run a batch above to generate some.
                    </div>
                ) : null}
            </div>
        </div>
    );
}


function RowCandidateCard({ row, busy, onPromote, onReject, onOpenRow }) {
    const [urlsByKind, setUrlsByKind] = useState({});
    const setUrl = (kind, v) => setUrlsByKind(s => ({ ...s, [kind]: v }));
    const pending = (row.candidates || []).filter(c => !c.reviewer_status);
    const mr = row.member_route || {};

    return (
        <div data-testid={`k3-1-backfill-card-${row.id}`} style={{
            padding: 14, marginBottom: 12, border: "1px solid #e5e7eb",
            borderRadius: 6, background: "white",
        }}>
            <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
                <button type="button" onClick={onOpenRow}
                        data-testid={`k3-1-backfill-open-${row.id}`}
                        style={{
                            background: "transparent", border: 0, padding: 0,
                            cursor: "pointer", textAlign: "left",
                            fontWeight: 600, fontSize: 15, color: "#111827",
                        }}>
                    {row.title}
                </button>
                <span style={{ fontSize: 12, color: "#6b7280" }}>
                    {[row.city, row.country].filter(Boolean).join(" · ")}
                </span>
                {(row.michelin_stars && row.michelin_stars > 0) ? (
                    <MichelinChip stars={row.michelin_stars} status={row.michelin_stars_status} />
                ) : null}
                <span data-testid={`k3-1-backfill-traffic-${row.id}`}
                      title="Click count on this row's /table card in the last 7 days"
                      style={{ marginLeft: "auto", fontSize: 11, color: "#1d4ed8", fontWeight: 600 }}>
                    {row.traffic_7d} click{row.traffic_7d === 1 ? "" : "s"} · 7d
                </span>
            </div>

            {/* Option B · member-render truth badge · founder-locked 15 Jun 2026 PM.
                Reads from table_route_log so the desk never wastes effort on a row
                that already serves members a working SevenRooms/Resy button. */}
            <MemberRouteBadge mr={mr} rowId={row.id} />

            {pending.map((c) => (
                <div key={c.kind} data-testid={`k3-1-backfill-candidate-${row.id}-${c.kind}`}
                     style={{
                         padding: 10, marginTop: 6,
                         background: "#fffbeb", border: "1px solid #fde68a",
                         borderRadius: 4,
                     }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: "#78350f" }}>{c.label}</span>
                        <a href={c.url} target="_blank" rel="noopener noreferrer"
                           data-testid={`k3-1-backfill-search-link-${row.id}-${c.kind}`}
                           style={{ fontSize: 11, color: "#92400e", textDecoration: "underline" }}>
                            Open search ↗
                        </a>
                        <span style={{ fontSize: 10, color: "#a16207", fontFamily: "monospace" }}>
                            country: {c.country_resolved} · {c.country_resolution_source}
                        </span>
                    </div>
                    <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
                        <input
                            value={urlsByKind[c.kind] || ""}
                            onChange={e => setUrl(c.kind, e.target.value)}
                            placeholder="Confirmed restaurant URL (strip ?utm_*/?source=michelin)"
                            data-testid={`k3-1-backfill-confirmed-url-${row.id}-${c.kind}`}
                            style={{ ...inputStyle, flex: 1, minWidth: 200, fontSize: 12 }}
                        />
                        <button onClick={() => onPromote(row.id, c.kind, urlsByKind[c.kind])}
                                disabled={busy || !urlsByKind[c.kind]}
                                data-testid={`k3-1-backfill-promote-${row.id}-${c.kind}`}
                                style={{
                                    background: urlsByKind[c.kind] ? "#059669" : "#9ca3af",
                                    color: "white", border: 0,
                                    padding: "6px 12px", borderRadius: 4, fontSize: 12,
                                    fontWeight: 600,
                                    cursor: (busy || !urlsByKind[c.kind]) ? "default" : "pointer",
                                }}>
                            Promote
                        </button>
                        <button onClick={() => onReject(row.id, c.kind, null)}
                                disabled={busy}
                                data-testid={`k3-1-backfill-reject-${row.id}-${c.kind}`}
                                style={{
                                    background: "white", color: "#a02020",
                                    border: "1px solid #fecaca",
                                    padding: "6px 12px", borderRadius: 4, fontSize: 12,
                                    cursor: "pointer",
                                }}>
                            Reject
                        </button>
                    </div>
                </div>
            ))}
            {!pending.length ? (
                <div style={{ fontSize: 11, color: "#9ca3af", padding: 6 }}>
                    All candidates on this row have been reviewed.
                </div>
            ) : null}
        </div>
    );
}


// ════════════════════════════════════════════════════════════════════
// Option B · member-render truth badge · founder-locked 15 Jun 2026 PM.
// ════════════════════════════════════════════════════════════════════
// Renders next to each K3.1 backfill candidate, showing what the
// MEMBER actually sees today via the Tier-0 → Tier-B → Tier-C
// resolution ladder (read from `table_route_log`). Purpose: prevent
// the desk from wasting effort on a row the sniffer already serves.
// `mr.status` is computed server-side by `_classify_member_route` in
// `routes/catalogue_table_backfill.py`.
function MemberRouteBadge({ mr, rowId }) {
    if (!mr || !mr.status) return null;
    const status = mr.status;
    let style, label, helper;
    if (status === "covered") {
        style = { bg: "#dcfce7", fg: "#14532d", bd: "#86efac" };
        label = `Members already see · ${mr.route_via} (${mr.resolved_via === "website_sniff" ? "website sniff" : "site provider"})`;
        helper = "K3.1 backfill is redundant here · the sniffer is doing the job. Skip unless the desk wants the rail explicitly stored.";
    } else if (status === "google_reserve") {
        style = { bg: "#fef3c7", fg: "#78350f", bd: "#fde68a" };
        label = `Members see · Google Reserve fallback (Places reservable)`;
        helper = "Marginal experience · K3.1 backfill would upgrade to a real third-party rail when matched.";
    } else if (status === "phone") {
        style = { bg: "#fef3c7", fg: "#78350f", bd: "#fde68a" };
        label = `Members see · Call route (Direct phone line)`;
        helper = "K3.1 backfill would upgrade to a one-tap booking rail.";
    } else if (status === "contact") {
        style = { bg: "#fef3c7", fg: "#78350f", bd: "#fde68a" };
        label = `Members see · Venue website (Book on their site)`;
        helper = "K3.1 backfill would upgrade to a real reservation rail.";
    } else if (status === "concierge") {
        style = { bg: "#fee2e2", fg: "#7f1d1d", bd: "#fecaca" };
        label = `Members see · Concierge® route (no public button)`;
        helper = "K3.1 backfill is most valuable for these rows.";
    } else { // no_log_yet
        style = { bg: "#f3f4f6", fg: "#374151", bd: "#d1d5db" };
        label = `Member route not yet logged (no recent /table render)`;
        helper = "Open the row to preview · the sniffer may serve a rail at render time.";
    }
    return (
        <div data-testid={`k3-1-backfill-member-route-${rowId}`}
             data-status={status}
             style={{
                 padding: "8px 10px", marginBottom: 6, borderRadius: 4,
                 background: style.bg, border: `1px solid ${style.bd}`,
                 color: style.fg, fontSize: 12,
             }}>
            <div style={{ fontWeight: 700 }}>{label}</div>
            <div style={{ fontSize: 11, opacity: 0.85, marginTop: 2 }}>{helper}</div>
            {mr.last_render_at ? (
                <div style={{ fontSize: 10, opacity: 0.7, marginTop: 2, fontFamily: "monospace" }}>
                    last render: {String(mr.last_render_at).slice(0, 19).replace("T", " ")}
                </div>
            ) : null}
        </div>
    );
}
